import { ChangeEvent, DragEvent, RefObject, useState } from "react";
import type { FileUploadConstraints } from "../types";
import { useDelayedAction } from "./useDelayedAction";
import {
  hasAcceptedFileMimeType,
  useValidation,
  ValidationErrorType,
} from "./useValidation";

const VALIDATION_ERROR_AUTO_HIDE_DELAY_MS = 3 * 1000; // 3 seconds

type UseDropzoneArgs = FileUploadConstraints & {
  onFilesSelected: (files: File[]) => void;
  fileInputRef: RefObject<HTMLInputElement | null>;
  validationErrorAutoHideDelayMs?: number;
};

export const useDropzone = ({
  onFilesSelected,
  fileInputRef,
  validationErrorAutoHideDelayMs = VALIDATION_ERROR_AUTO_HIDE_DELAY_MS,
  maxFileSizeBytes,
  acceptedFileExtensions,
  acceptedMimeTypes,
}: UseDropzoneArgs) => {
  const [isDragging, setIsDragging] = useState(false);

  const {
    validateFile,
    validationErrorType,
    setValidationErrorType,
    clearValidationError,
  } = useValidation({
    maxFileSizeBytes,
    acceptedFileExtensions,
    acceptedMimeTypes,
  });

  const {
    run: runValidationErrorAutoHide,
    cancel: cancelValidationErrorAutoHide,
  } = useDelayedAction({
    delay: validationErrorAutoHideDelayMs,
    action: clearValidationError,
  });

  const openFilePicker = () => fileInputRef.current?.click();

  const handleFiles = (files: File[]) => {
    if (files.length === 0) {
      return;
    }

    for (const file of files) {
      const nextValidationMessage = validateFile(file);
      if (!nextValidationMessage.valid) {
        setValidationErrorType(nextValidationMessage.type);
        return runValidationErrorAutoHide();
      }
    }

    clearValidationError();
    onFilesSelected(files);
  };

  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    handleFiles(files);
    event.target.value = "";
  };

  const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsDragging(false);

    const files = Array.from(event.dataTransfer.files);
    handleFiles(files);
  };

  const handleDragOver = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsDragging(true);
    clearValidationError();
    cancelValidationErrorAutoHide();

    for (const item of event.dataTransfer.items) {
      if (
        item.kind === "file" &&
        !hasAcceptedFileMimeType(item, acceptedMimeTypes)
      ) {
        setValidationErrorType(ValidationErrorType.UnsupportedFileType);
        break;
      }
    }
  };

  const handleDragLeave = (event: DragEvent<HTMLButtonElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      clearValidationError();
      setIsDragging(false);
    }
  };

  return {
    isDragging,
    validationErrorType,
    openFilePicker,
    handleFileInputChange,
    handleDrop,
    handleDragOver,
    handleDragLeave,
  };
};
